import type { BriefSource, Note, Rendition, Segment, SegmentBrief } from "@scenix/core";
import type { DraftSegment } from "./api";

/**
 * The arithmetic behind the scrub bar, kept apart from the component so it can
 * be tested without a DOM.
 *
 * Everything here works in timeline seconds. Pixels appear only at the two
 * conversion functions, which is what lets the same selection survive a resize,
 * a zoom, and a section going from planned to made (ADR 000025).
 */

/** A half-open span of the timeline, in seconds. */
export interface Span {
  startSec: number;
  endSec: number;
}

/**
 * Selections snap to this grid, which is fine enough to land inside a syllable
 * and coarse enough that dragging does not produce 3.1400000000000006.
 */
export const SNAP_SEC = 0.01;

export function snap(seconds: number): number {
  return Math.round(seconds / SNAP_SEC) * SNAP_SEC;
}

/** Where a point in seconds sits along a bar of the given width. */
export function secondsToX(seconds: number, durationSec: number, width: number): number {
  if (durationSec <= 0) return 0;
  return (clamp(seconds, 0, durationSec) / durationSec) * width;
}

/** What second a pixel offset along the bar refers to. */
export function xToSeconds(x: number, durationSec: number, width: number): number {
  if (width <= 0) return 0;
  return snap(clamp((x / width) * durationSec, 0, durationSec));
}

export function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

/**
 * A drag in either direction becomes a forward span.
 *
 * Dragging right to left is how people select the thing they just watched go
 * past, so it has to mean the same as dragging left to right.
 */
export function spanFromDrag(anchorSec: number, cursorSec: number): Span {
  return anchorSec <= cursorSec
    ? { startSec: anchorSec, endSec: cursorSec }
    : { startSec: cursorSec, endSec: anchorSec };
}

/**
 * Whether a span is worth acting on.
 *
 * A click is a drag of zero width. Without a floor, every click on the bar
 * would open the note menu on an empty range.
 */
export const MIN_SELECTION_SEC = 0.05;

export function isSelection(span: Span | null): span is Span {
  return span !== null && span.endSec - span.startSec >= MIN_SELECTION_SEC;
}

/** Half-open overlap: a span ending exactly where another starts does not. */
export function overlaps(a: Span, b: Span): boolean {
  return a.startSec < b.endSec && b.startSec < a.endSec;
}

/** The segments a selection touches, which is what a note is really about. */
export function segmentsIn(segments: readonly Segment[], span: Span): Segment[] {
  return segments.filter((segment) => overlaps(segment, span));
}

/** The notes that fall inside a span, for showing what has already been said. */
export function notesIn(notes: readonly Note[], span: Span): Note[] {
  return notes.filter((note) => overlaps(note.range, span));
}

/**
 * Timecode as mm:ss.hh.
 *
 * Hundredths because that is the resolution the selection snaps to, and a
 * readout coarser than the thing it describes is a readout you cannot trust.
 */
export function timecode(seconds: number): string {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  const rest = safe - minutes * 60;
  const whole = Math.floor(rest);
  const hundredths = Math.round((rest - whole) * 100);

  // Rounding 59.999 gives 60 hundredths, which would render as 00:59.100.
  if (hundredths === 100) return timecode(minutes * 60 + whole + 1);

  return `${pad(minutes)}:${pad(whole)}.${pad(hundredths)}`;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * The part of a segment covering a sub-span of it, with every source in its
 * brief sliced to match.
 *
 * A segment maps its span on the timeline onto a range of each source it draws
 * on. Keeping only part of the segment therefore has to keep the matching part
 * of each, or the picture silently becomes footage from the wrong moment.
 */
/**
 * A segment as the server will take it back.
 *
 * The state, the current rendition and the timeline are the server's, and the
 * API rejects unknown fields outright, so handing a whole segment back is a 400
 * rather than a few fields quietly ignored. Every edit goes through here.
 */
export function toDraft(segment: Segment): DraftSegment {
  return {
    id: segment.id,
    track: segment.track,
    startSec: segment.startSec,
    endSec: segment.endSec,
    brief: segment.brief,
    label: segment.label,
    derivedFromId: segment.derivedFromId,
  };
}

function slice(segment: Segment, from: number, to: number): DraftSegment {
  const span = segment.endSec - segment.startSec;

  const piece: DraftSegment = { ...toDraft(segment), startSec: from, endSec: to };

  if (span <= 0 || segment.brief.sources.length === 0) return piece;

  piece.brief = {
    ...segment.brief,
    sources: segment.brief.sources.map((source) => {
      // Rate is 1 for everything today. Written out so a source played at half
      // speed slices correctly the day one exists.
      const rate = (source.range.endSec - source.range.startSec) / span;
      return {
        ...source,
        range: {
          startSec: source.range.startSec + (from - segment.startSec) * rate,
          endSec: source.range.startSec + (to - segment.startSec) * rate,
        },
      };
    }),
  };

  return piece;
}

/**
 * What is left of a segment once a span is taken out of it.
 *
 * Zero pieces if the span swallows it, one if the span clips an end, two if the
 * span goes through the middle. The middle case is a genuine split: the two
 * halves show footage from either side of the removed part, and pretending they
 * are one segment would mean claiming a continuous shot where there is a cut.
 *
 * The first piece keeps the segment's identity; a piece that did not exist
 * before has no id, and the server mints one.
 */
function remove(segment: Segment, span: Span): DraftSegment[] {
  if (!overlaps(segment, span)) return [toDraft(segment)];
  if (segment.startSec >= span.startSec && segment.endSec <= span.endSec) return [];

  const pieces: DraftSegment[] = [];
  if (segment.startSec < span.startSec) {
    pieces.push(slice(segment, segment.startSec, span.startSec));
  }
  if (segment.endSec > span.endSec) {
    const tail = slice(segment, span.endSec, segment.endSec);
    if (pieces.length > 0) {
      // A piece that did not exist before gets no id, and records what it came
      // from so a note left before the split can still find it (ADR 000025).
      delete tail.id;
      tail.derivedFromId = segment.id;
    }
    pieces.push(tail);
  }
  return pieces;
}

/**
 * Applies a trim: the span is removed and the gap stays.
 *
 * The difference from a cut is what happens downstream. A trim leaves it where
 * it was, which is what you want when the timing after this point is already
 * settled against narration or music.
 */
export function applyTrim(segments: readonly Segment[], span: Span): DraftSegment[] {
  return segments.flatMap((segment) => remove(segment, span));
}

/** Whether a segment has a picture to show, or is still part of the storyboard. */
export function isPlanned(segment: Segment): boolean {
  return segment.state === "planned";
}

/**
 * The rendition a segment stands on: the one the server pointed it at, not the
 * newest one in the list.
 *
 * A segment's state is derived from this one rendition and no other (ADR
 * 000025), so picking a different one would put a state on screen the API never
 * said, which is the whole failure the derived state exists to avoid.
 */
export function pictureOf(
  renditions: readonly Rendition[],
  segment: Segment,
): Rendition | undefined {
  if (!segment.renditionId) return undefined;
  return renditions.find((rendition) => rendition.id === segment.renditionId);
}

/**
 * How long one of a segment's stills stays on screen: an equal slice of the
 * segment, so the stills fill the shot and no other length has to be reconciled.
 *
 * The same division the renderer makes, kept under the same name (`stillHold`
 * in apps/api/internal/render/animatic.go). A segment carrying three stills is
 * a shot somebody planned as three frames (ADR 000065), and anything standing
 * in for that render has to divide the same way or it shows one frame held for
 * nine seconds where the file cuts twice. Pacing is exactly what somebody is
 * watching the chain to judge.
 *
 * The cases are shared in packages/schema/still-hold-fixtures.json, which both
 * suites read, so a second division cannot quietly appear on either side.
 */
export function stillHold(segmentSec: number, count: number): number {
  if (count <= 1) return segmentSec;
  return segmentSec / count;
}

/**
 * Which still is on screen, that far into the segment.
 *
 * Undefined when the segment has no stills: there is no image to ask for, which
 * is not the same as asking for the first of none.
 */
export function stillAt(
  intoSegmentSec: number,
  segmentSec: number,
  count: number,
): number | undefined {
  if (count <= 0) return undefined;
  // A segment covering no time has no slices to divide into, and the renderer
  // refuses one outright. The first still is a better answer than a division by
  // zero reaching for the address of an image.
  if (segmentSec <= 0) return 0;
  // The last still carries the end of the shot, so a playhead resting exactly
  // on the segment's end is on the final frame rather than one past the list.
  return clamp(
    Math.floor(Math.max(intoSegmentSec, 0) / stillHold(segmentSec, count)),
    0,
    count - 1,
  );
}

/** What the chain has to hand over as one file, and what it no longer has. */
export interface Assembly {
  /** The assembly of the chain as it stands, once somebody has asked for one. */
  current: Rendition | undefined;
  /** The newest assembly of a chain that has since moved on. */
  superseded: Rendition | undefined;
  /** The last attempt, when it failed and so left nothing to hand over. */
  failed: Rendition | undefined;
}

/**
 * Tells the assembly that is this chain from one that was this chain.
 *
 * `fingerprint` is the timeline's own `assemblyFingerprint`: what the API would
 * make an assembly out of if asked right now. Matching against it rather than
 * recomputing the hash here is the point, because a copy of that rule on this
 * side of the wire would drift from the one the request path enforces.
 *
 * The distinction matters because a superseded assembly plays perfectly. It is
 * simply a version behind the timeline it claims to be, which is exactly the
 * file ADR 000061 refuses to make and therefore the file nothing should offer as
 * the deliverable. A failed one is neither, and is reported on its own: it is
 * still the last thing that happened, and the alternative is a page that goes
 * back to the button it started as and never says why (issue #390).
 */
export function assemblyOf(renditions: readonly Rendition[], fingerprint: string): Assembly {
  // Newest first. An earlier assembly of the same chain is the same file made
  // twice, so only the latest of anything here is worth considering.
  const assemblies = renditions.filter((rendition) => !rendition.segmentId).reverse();
  // A failure is no file, so it can be neither the deliverable nor a version
  // behind one. Both of those questions are asked of what was actually made.
  const made = assemblies.filter((rendition) => rendition.status !== "failed");

  const current = fingerprint
    ? made.find((rendition) => rendition.briefFingerprint === fingerprint)
    : undefined;

  if (current) return { current, superseded: undefined, failed: undefined };

  // Only the newest attempt. A failure the chain has since assembled past is
  // not news, and an assembly still being made is not a failure yet.
  const newest = assemblies[0];

  return {
    current: undefined,
    superseded: made[0],
    failed: newest?.status === "failed" ? newest : undefined,
  };
}

/**
 * Whether anything on the chain is still being made, which is what makes a
 * timeline worth re-reading on a timer. Rendering happens in a worker, so a
 * page left open should catch up on its own rather than needing a reload.
 */
export function anyRendering(renditions: readonly Rendition[]): boolean {
  return renditions.some(
    (rendition) => rendition.status === "queued" || rendition.status === "rendering",
  );
}

/**
 * How much of the chain is made, as a fraction. This is what a project stage
 * cannot express: a long video is routinely part cut and part storyboard
 * (ADR 000025).
 */
export function progress(segments: readonly Segment[]): number {
  let planned = 0;
  let ready = 0;

  for (const segment of segments) {
    if (segment.track !== 0) continue;
    const length = segment.endSec - segment.startSec;
    planned += length;
    if (segment.state === "ready" || segment.state === "stale") ready += length;
  }

  return planned > 0 ? ready / planned : 0;
}

/**
 * A new segment holding a place in the chain. Direction and a length is a
 * complete brief, which is what lets a section be planned before the footage
 * for it exists (ADR 000026).
 */
export function plannedSegment(startSec: number, endSec: number, direction: string): DraftSegment {
  return {
    track: 0,
    startSec,
    endSec,
    brief: { kind: "generated", direction, narration: "", sources: [], stills: [] },
  };
}

/**
 * Attaches footage to a brief, which is what "drag a video onto the timeline"
 * means underneath (ADR 000026): the segment now draws on real material rather
 * than only naming what should happen, so its kind moves from `generated` to
 * `asset` along with the source.
 */
export function withSource(brief: SegmentBrief, source: BriefSource): SegmentBrief {
  return { ...brief, kind: "asset", sources: [...brief.sources, source] };
}

/**
 * Rewrites the words in a brief and leaves everything else it carries alone.
 *
 * Spread rather than rebuilt, because a brief holds more than the browser can
 * author: the sources, the stills, a layout and the notes aimed at the renderer
 * all have to survive somebody correcting a typo in the direction (ADR 000026).
 */
export function withWords(
  brief: SegmentBrief,
  words: { direction: string; narration: string },
): SegmentBrief {
  return { ...brief, direction: words.direction, narration: words.narration };
}

/**
 * Whether a brief would leave its segment with no instruction at all.
 *
 * Mirrors `SegmentBrief.Empty` in the Go domain, which refuses such a write with
 * 400: a place in the chain nothing says how to fill can neither be rendered nor
 * reasoned about. Checked here so the Save button goes flat rather than sending
 * an edit that is certain to come back refused.
 */
export function saysNothing(brief: SegmentBrief): boolean {
  return (
    brief.direction.trim() === "" &&
    brief.narration.trim() === "" &&
    brief.sources.length === 0 &&
    !brief.layout
  );
}

/**
 * Applies a cut: the span is removed and everything after it slides back.
 *
 * Done in the browser rather than waiting on the agent, because a cut is exact.
 * Nothing has to be interpreted, so the timeline can be right immediately and
 * the round trip is only persistence (ADR 000023).
 */
export function applyCut(segments: readonly Segment[], span: Span): DraftSegment[] {
  const removed = span.endSec - span.startSec;

  return applyTrim(segments, span).map((piece) =>
    piece.startSec >= span.endSec
      ? { ...piece, startSec: piece.startSec - removed, endSec: piece.endSec - removed }
      : piece,
  );
}
